import { useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Mail,
  MessageSquare,
  Phone,
  User,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { lines, sz, txt, useContent } from "@/content/ContentContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  message: "",
};

const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const c = useContent();
  const cf = c.global.contactForm;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setError("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setStatus("error");
      setError("Please enter a valid email address.");
      return;
    }

    setStatus("sending");
    setError("");

    try {
      await axios.post(`${API_BASE}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      setStatus("error");
      setError(
        "Something went wrong while sending your message. Please try again or email us directly."
      );
    }
  };

  return (
    <section className="contactform" id="message">
      <Reveal className="cf-inner" y={22}>
        <div className="cf-left">
          <h2 className="section-title" style={sz(cf.title1)}>
            {txt(cf.title1)}
            <br />
            {txt(cf.title2)}{" "}
            <span className="text-green" style={sz(cf.accent)}>{txt(cf.accent)}</span>.
          </h2>
          <p className="section-para" style={sz(cf.paragraph)}>
            {lines(cf.paragraph)}
          </p>
          <ul className="cf-points">
            {cf.points.map((point, index) => (
              <li key={index} style={sz(point)}>{txt(point)}</li>
            ))}
          </ul>
        </div>
        <form
          className="cf-form"
          onSubmit={handleSubmit}
          noValidate
          data-testid="contact-form"
        >
          <div className="cf-row">
            <label className="cf-field">
              <span className="cf-label">Name *</span>
              <span className="cf-input-wrap">
                <User className="cf-input-icon" strokeWidth={1.6} />
                <input
                  className="cf-input"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  data-testid="contact-name-input"
                  required
                />
              </span>
            </label>
            <label className="cf-field">
              <span className="cf-label">Email *</span>
              <span className="cf-input-wrap">
                <Mail className="cf-input-icon" strokeWidth={1.6} />
                <input
                  className="cf-input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  data-testid="contact-email-input"
                  required
                />
              </span>
            </label>
          </div>
          <div className="cf-row">
            <label className="cf-field">
              <span className="cf-label">Company</span>
              <span className="cf-input-wrap">
                <Building2 className="cf-input-icon" strokeWidth={1.6} />
                <input
                  className="cf-input"
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  placeholder="Company name"
                  data-testid="contact-company-input"
                />
              </span>
            </label>
            <label className="cf-field">
              <span className="cf-label">Phone</span>
              <span className="cf-input-wrap">
                <Phone className="cf-input-icon" strokeWidth={1.6} />
                <input
                  className="cf-input"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  data-testid="contact-phone-input"
                />
              </span>
            </label>
          </div>
          <label className="cf-field">
            <span className="cf-label">Message *</span>
            <span className="cf-input-wrap cf-textarea-wrap">
              <MessageSquare className="cf-input-icon" strokeWidth={1.6} />
              <textarea
                className="cf-textarea"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="How can we help? Number of devices, current setup, timelines..."
                data-testid="contact-message-input"
                required
              />
            </span>
          </label>
          {status === "success" && (
            <p
              className="cf-status cf-status-success"
              data-testid="contact-success-message"
            >
              <CheckCircle2 className="cf-status-icon" strokeWidth={1.8} />
              Thank you. Your message has been sent and we will be in touch
              shortly.
            </p>
          )}
          {status === "error" && (
            <p
              className="cf-status cf-status-error"
              data-testid="contact-error-message"
            >
              <AlertCircle className="cf-status-icon" strokeWidth={1.8} />
              {error}
            </p>
          )}
          <button
            type="submit"
            className="btn-dark cf-submit"
            disabled={status === "sending"}
            data-testid="contact-submit-button"
          >
            {status === "sending" ? "Sending..." : txt(cf.submitLabel)}
          </button>
        </form>
      </Reveal>
    </section>
  );
};

export default ContactForm;
